// bluaIQ — TASK-WEB-8: the owner-authorized C-1500 demo project.
//
// AUTHORITY: owner designation `exposure-demo-project-c1500` (evidence_class
// user_truth, designated by Maria 2026-07-08), recorded verbatim in the TAKEOFF
// repo artifact exposure_gate_designations_20260708_001 (commit 10aa3d9): the
// second plan set (C-1500) is the demo project — the owner authorizes HER OWN
// project data for her website demo. The benchmark (C-1800/Spring Garden)
// remains barred by the standing fixture-isolation rule, and the SYNTHETIC
// fixtures (Houses Alpha/Beta) are preserved untouched for tests.
//
// The payload file demoProjectC1500PayloadV3.json is a VERBATIM copy of the
// TAKEOFF export takeoff_diagnostic_artifacts/c1500_demo_payload_reexport_20260716_001/
// demo_project_c1500_payload_v3.json (TASK-71, post-70B re-export; authorized by
// Codex after TASK-71 PASS and swapped in by TASK-WEB-41). Byte-for-byte identity
// is pinned by a sha256 smoke test against DEMO_PROJECT_C1500_PAYLOAD_SHA256.
//
// WHAT CHANGED, AND WHY IT LOOKS "WORSE" (TASK-WEB-41): the demo now runs the
// CURRENT generic engine instead of TASK-48's bespoke full-coverage requant.
//   TASK-48 (superseded): 32 lines, coverage 1.0 — openings GD1/W1/W3/D5 were
//     AUTO-CONFIRMED by the naive crosscheck note "schedule and placed marks
//     match", i.e. the schedule confirming itself.
//   Now (TASK-70B frozen baseline): 37 lines, 29 quantity-bearing, 8 blocked
//     visible — GD1/W1/W3/D5 arrive PENDING carrying the sealed bilingual
//     view-ownership copy (9 lines carry it), and the naive confirmation note is
//     GONE by design.
// That is the sealed product posture, not a regression: a cheap confirmation is
// technical debt, not a feature — honest pending beats false confirmed. The
// Shell ships COPY.demoEngineNote next to the demo so a reader does not mistake
// the engine's honesty for a broken demo.
//
// Lineage (from the export manifest): probe pin 70A 6d53bad2b284da60…;
// composition pin 70B bd94da516fdc0551… — the demo differs from the pin ONLY in
// read_run_id (demo-c1500-20260716-003), which is a gate, not a content delta.
//
// GENERATION (owner designation `exposure-generation-by-button`): quantification
// runs from an explicit button, nothing automatic. FOR THIS DEMO PHASE the
// button loads THIS static, pre-exported payload after contract-v3 validation.
// Wiring the button to REAL engine generation (upload → engine run → payload)
// is deliberately deferred to a later phase and will need its own authorization
// — this module performs no I/O, no generation, no writes.

import type { WebsiteTakeoffPayloadV3 } from '../../shared/websitePayloadContractV3'
import type { BilingualLabel } from '../../shared/websitePayloadContractV2'
import demoProjectC1500PayloadJson from './demoProjectC1500PayloadV3.json'

/** sha256 of the verbatim payload file, as exported by the TAKEOFF engine.
 *  TASK-WEB-41: bumped to the post-70B re-export (see the module header). The
 *  superseded exports are preserved here as history, never as fallbacks:
 *    TASK-47 first export  585c97317f64c23c9b6453c09879e234f8e7ccb765408326f3eead086ae33095
 *    TASK-48 full-coverage 7927df853b4409a70c8d68ec99d8852037c658d43d13b1cb608436ec4fa451e1 */
export const DEMO_PROJECT_C1500_PAYLOAD_SHA256 =
  '1f1eecff22c6b9d1b05be9f3e8a87a3a67aba55c57e03354815227bd42ac83b2'

export const DEMO_PROJECT_C1500_ID = 'demo-c1500'

export const DEMO_PROJECT_C1500_LABEL: BilingualLabel = {
  en: 'Demo project (C-1500 — owner data)',
  es: 'Proyecto demo (C-1500 — datos de la propietaria)',
}

/** The real, owner-authorized v3 payload (32 lines, Plan QA with the owner's 4
 *  real issues). Loaded statically at build time; the Shell only SHOWS it after
 *  the explicit "Cuantificar planos" button click + v3 validation. */
export function getDemoProjectC1500Payload(): WebsiteTakeoffPayloadV3 {
  return demoProjectC1500PayloadJson as unknown as WebsiteTakeoffPayloadV3
}
